import React from 'react';
import { t } from '../../../../i18n';
import { useNavigate } from 'react-router-dom';
import { DeliveryDetailPath } from '../../../../enums/DeliveryNavigatePathEnum';
import { getLocalizedAccessCodeTypeName } from '../utils/accessCodeUtils';
import { TYPE_SORT_ORDER } from '../utils/accessCodeUtils';
import accessCodeSvg from '../../../../assets/icons/svgs/accessCode.svg';
import searchSvg from '../../../../assets/icons/svgs/search.svg';

// 门禁信息类型定义
export interface AccessCode {
    id: number;
    type: string; // 门禁类型，如 "MainDoor", "Gate" 等
    code: string; // 门禁code
    status?: number | null; // 状态枚举值：0-待验证，1-可用，2-不可用
    createdAt?: number; // 创建时间（unix timestamp秒数）
    updatedAt?: number; // 更新时间（unix timestamp秒数）
    validFeedbackCount?: number; // 有效反馈数量
    invalidFeedbackCount?: number; // 无效反馈数量
    orderAddressId?: number; // 订单地址ID
    latitude?: number; // 纬度
    longitude?: number; // 经度
    currentDriverCreatedAt?: number; // 当前司机创建该code的时间（unix timestamp秒数，用于前端24小时频率限制校验）
    currentDriverUpdatedAt?: number; // 当前司机最后更新该code的时间（unix timestamp秒数，用于前端24小时频率限制校验）
}

interface AccessInfoDisplayProps {
    accessCodes?: AccessCode[]; // 完整的门禁码列表（用于详情页）
    addressId?: number;
    trackingNumber?: string;
    address?: string; // 地址文本
    addressLat?: number; // 地址纬度
    addressLon?: number; // 地址经度
    orderId?: number; // 订单ID（用于创建门禁码）
    onAddCode?: () => void; // 新增code的回调
    showAddButton?: boolean; // 是否显示新增按钮
    isLoading?: boolean; // 是否正在加载（用于避免闪烁）
    fromMapMode?: boolean; // 是否来自地图模式
    deliveryTaskOrderId?: number; // 订单ID（用于返回导航）
    onNavigateToAccessCodeDetail?: (params: {
        orderId: number;
        addressId?: number;
        trackingNumber?: string;
        address?: string;
        addressLat?: number;
        addressLon?: number;
    }) => void; // 导航到门禁详情的回调（地图模式使用）
    isFeatureEnabled?: boolean; // 当前司机是否启用门禁码功能（灰度控制）
}

const AccessInfoDisplay: React.FC<AccessInfoDisplayProps> = ({
    accessCodes = [],
    addressId,
    trackingNumber,
    address,
    addressLat,
    addressLon,
    orderId,
    onAddCode,
    showAddButton = true,
    isLoading = false,
    fromMapMode = false,
    deliveryTaskOrderId,
    onNavigateToAccessCodeDetail,
    isFeatureEnabled = false // 默认禁用，需要显式传入 true 才启用
}) => {
    const navigate = useNavigate();

    // 如果正在加载，不显示任何内容（避免闪烁）
    if (isLoading) {
        return null;
    }

    // 如果功能未启用，不显示任何内容
    if (!isFeatureEnabled) {
        return null;
    }

    // 统一的点击处理函数
    const handleClick = () => {
        if (!trackingNumber || !orderId) {
            return;
        }
        
        // 如果在地图模式下且有回调，使用回调切换视图（在抽屉内显示）
        if (fromMapMode && onNavigateToAccessCodeDetail) {
            console.log('地图模式：使用回调切换视图', { fromMapMode, hasCallback: !!onNavigateToAccessCodeDetail });
            onNavigateToAccessCodeDetail({
                orderId,
                addressId,
                trackingNumber,
                address,
                addressLat,
                addressLon
            });
        } else {
            // 否则使用路由跳转（非地图模式）
            console.log('非地图模式：使用路由跳转', { fromMapMode, hasCallback: !!onNavigateToAccessCodeDetail });
            navigate(
                DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL_ACCESS_INFO.replace(
                    ':trackingNumber',
                    trackingNumber
                ),
                {
                    state: {
                        addressId,
                        trackingNumber,
                        accessCodes,
                        address,
                        addressLat,
                        addressLon,
                        orderId, // 传递订单ID
                        fromMapMode, // 传递是否来自地图模式
                        deliveryTaskOrderId // 传递订单ID（用于返回导航）
                    }
                }
            );
        }
    };

    // 如果有code，显示code信息
    if (accessCodes && accessCodes.length > 0) {
        // 前端分组：按类型分组，生成显示文本
        const codesByType = accessCodes.reduce((acc, code) => {
            if (!acc[code.type]) {
                acc[code.type] = [];
            }
            acc[code.type].push(code);
            return acc;
        }, {} as Record<string, AccessCode[]>);

        // 格式化显示数据：每个类型只取第一个code，不同类型用/隔开
        // 注意：后端已经按类型排序，每个类型内部也已排序，所以直接取第一个即可
        // 但为了保持类型顺序一致，还是按类型排序（防御性编程）
        const sortedTypes = Object.keys(codesByType).sort((a, b) => {
            const orderA = TYPE_SORT_ORDER[a] || 999;
            const orderB = TYPE_SORT_ORDER[b] || 999;
            return orderA - orderB;
        });
        
        // 每个类型只取第一个code（后端已排序，直接取第一个），格式：{类型}: {code}
        const displayItems: Array<{ type: string; code: string }> = [];
        sortedTypes.forEach(type => {
            const codes = codesByType[type];
            if (codes.length > 0) {
                // 后端已经排序好了，直接取第一个
                const firstCode = codes[0].code;
                // 使用国际化文本显示门禁类型
                const localizedType = getLocalizedAccessCodeTypeName(type, t);
                displayItems.push({ type: localizedType, code: firstCode });
            }
        });

        return (
            <div className="flex items-center gap-2">
                <div 
                    className="text-sm break-words cursor-pointer active:opacity-70 transition-opacity line-clamp-2"
                    onClick={handleClick}
                >
                    {displayItems.map((item, index) => (
                        <span key={index}>
                            <span className="text-gray-400">{item.type}</span>
                            {' '}
                            <span className="text-gray-600 font-semibold">{item.code}</span>
                            {index < displayItems.length - 1 && (
                                <span className="text-gray-400"> | </span>
                            )}
                        </span>
                    ))}
                </div>
                <button
                    onClick={handleClick}
                    className="flex items-center justify-center flex-shrink-0 p-1"
                    aria-label="View access code details"
                >
                    <img 
                        src={searchSvg} 
                        alt="Search" 
                        className="w-5 h-5"
                    />
                </button>
            </div>
        );
    }

    // 无code模式
    return (
        <div className="flex items-center gap-2">
            <span 
                className="text-sm text-gray-500 cursor-pointer active:opacity-70 transition-opacity"
                onClick={onAddCode}
            >
                {t('pages.delivery.orderDetail.noAccessInfo')}
            </span>
            {showAddButton && (
                <button
                    onClick={onAddCode}
                    className="flex items-center justify-center text-blue-500 hover:text-blue-600 transition-colors p-1"
                    aria-label={t('pages.delivery.orderDetail.addCode')}
                >
                    <img 
                        src={accessCodeSvg} 
                        alt="Access Code" 
                        className="w-5 h-5"
                    />
                </button>
            )}
        </div>
    );
};

export default AccessInfoDisplay;

